import { keyBy } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Line, LineDirectory, StationInLineInsertion } from '@paragon/contract-library';

import { stationsSlice } from './stationsSlice';

interface LinesState {
  readonly status: 'loading' | 'loaded' | 'catastrophicallyFailed';

  readonly lines: Record<string, Line>;
  readonly stationInsertions: Record<string, readonly StationInLineInsertion[]>;

  readonly directories: Record<string, LineDirectory>;
}

const initialState: LinesState = {
  status: 'loading',

  lines: {},
  stationInsertions: {},
  directories: {},
};

export const linesSlice = createSlice({
  name: 'lines',
  initialState,
  reducers: {
    loadedFromServer(
      state,
      action: PayloadAction<{
        lines: Line[];
        stationRelationships: Record<string, readonly StationInLineInsertion[]>;
        directories: LineDirectory[];
      }>,
    ) {
      state.status = 'loaded';

      state.lines = keyBy(action.payload.lines, (line) => line.guid);
      state.stationInsertions = castDraft(action.payload.stationRelationships);
      state.directories = keyBy(action.payload.directories, (directory) => directory.guid);
    },

    loadingCatastrophicallyFailed(state) {
      state.status = 'catastrophicallyFailed';
    },

    singleDirectoryEdited(state, action: PayloadAction<LineDirectory>) {
      state.directories[action.payload.guid] = action.payload;
    },

    singleDirectoryDeleted(state, action: PayloadAction<{ guid: string; parentGuid?: string }>) {
      delete state.directories[action.payload.guid];

      for (const directory of Object.values(state.directories)) {
        if (directory.parentGuid === action.payload.guid) {
          directory.parentGuid = action.payload.parentGuid;
        }
      }

      for (const line of Object.values(state.lines)) {
        if (line.parentGuid === action.payload.guid) {
          line.parentGuid = action.payload.parentGuid;
        }
      }
    },

    singleLineEdited(state, action: PayloadAction<Line>) {
      state.lines[action.payload.guid] = action.payload;
    },

    singleLineDeleted(state, action: PayloadAction<{ guid: string; parentGuid?: string }>) {
      delete state.lines[action.payload.guid];

      if (action.payload.guid in state.stationInsertions) {
        if (action.payload.parentGuid) {
          const stationInsertions = state.stationInsertions[action.payload.guid];

          if (state.stationInsertions[action.payload.parentGuid] == null) {
            state.stationInsertions[action.payload.parentGuid] = [];
          }

          for (const insertion of stationInsertions) {
            state.stationInsertions[action.payload.parentGuid].push(insertion);
          }
        } else {
          delete state.stationInsertions[action.payload.guid];
        }
      }
    },

    stationInserted(state, action: PayloadAction<{ lineGuid: string; insertion: StationInLineInsertion }>) {
      if (action.payload.lineGuid in state.stationInsertions) {
        state.stationInsertions[action.payload.lineGuid].push(action.payload.insertion);
      } else {
        state.stationInsertions[action.payload.lineGuid] = [action.payload.insertion];
      }
    },

    stationUnInserted(state, action: PayloadAction<{ lineGuid: string; stationGuid: string }>) {
      if (action.payload.lineGuid in state.stationInsertions) {
        state.stationInsertions[action.payload.lineGuid] = state.stationInsertions[action.payload.lineGuid].filter(
          (insertion) => insertion.guid !== action.payload.stationGuid,
        );
      }
    },
  },

  extraReducers(builder) {
    builder.addCase(stationsSlice.actions.singleStationCreated, (state, action) => {
      if (!action.payload.parentGuid) return;

      if (!state.stationInsertions[action.payload.parentGuid]) {
        state.stationInsertions[action.payload.parentGuid] = [];
      }

      state.stationInsertions[action.payload.parentGuid].push({
        guid: action.payload.station.guid,
        xIndex: 0,
        yIndex: 0,
      });
    });

    builder.addCase(stationsSlice.actions.singleStationDeleted, (state, action) => {
      for (const lineGuid of Object.keys(state.stationInsertions)) {
        state.stationInsertions[lineGuid] = state.stationInsertions[lineGuid].filter(
          (insertion) => insertion.guid !== action.payload.guid,
        );
      }
    });
  },
});
